// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button } from '@imprivata-cloud/components';
import { Alert, Card, Flex, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useGetTenantQuery, usePostGrantMutation } from '../../api/hooks';
import { useConsentMutation } from '../../api/hooks/consentMutation';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../components/loading/Loading';

const ConsentPage = () => {
  const { t } = useTranslation();
  const { tenantName } = useGetTenantQuery();
  const { mutate: consentMutate } = useConsentMutation();
  const { postGrant, isPending } = usePostGrantMutation({
    onSuccess: () => {
      // Grant API success, browser will handle redirect
    },
  });
  const [redirecting, setRedirecting] = useState(false);
  const [showCloseMessage, setShowCloseMessage] = useState(false);

  // Show close message after 2 seconds when redirecting
  useEffect(() => {
    if (redirecting) {
      const timer = setTimeout(() => {
        setShowCloseMessage(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [redirecting]);

  const handleConsent = (granted: boolean) => {
    setRedirecting(true);
    consentMutate(granted, {
      onSuccess: () => {
        postGrant();
      },
    });
  };

  if (redirecting || isPending) {
    // Show loading spinner for 2 seconds before showing close message
    if (!showCloseMessage) {
      return (
        <Flex align="center" justify="center" vertical gap={16} className="h-100" data-testid="consent-loading">
          <LoadingSpinner />
        </Flex>
      );
    }

    // After 2 seconds, show the redirect message
    return (
      <Flex align="center" justify="center" vertical gap={16} className="h-100">
        <Typography.Title
          level={3}
          className="text-center"
          data-testid="consent-title-confirmation"
          style={{ margin: '30px 10px' }}
        >{t('consentPage.youre-being-redirected-to-the-application')}</Typography.Title>
        <Alert
          message={t('consentPage.you-may-close-this-window')}
          type="info"
          className="w-100 impr-alert impr-alert-info"
          icon={<InfoCircleOutlined style={{ color: '#426DA9', fontSize: 32, margin: '0 8px' }} />}
          showIcon={true}
          style={{ marginBottom: '8px', border: '2px solid #426DA9', color: '#426DA9' }}
          data-testid="consent-redirect-alert"
        />
      </Flex>
    );
  }
  return (
    <Flex align="center" justify="center" vertical gap={16} className="h-100">
      <Typography.Title level={3} className="text-center" data-testid="consent-title">{t('consentPage.create-a-mychart-account')}</Typography.Title>
      <Typography.Text
        style={{ padding: '24px 0', textAlign: 'center', display: 'block' }}
        data-testid="consent-description"
      >
        {t('consentPage.consent-description', { tenantName })}
      </Typography.Text>
      <Card className="w-100 bg-gray" data-testid="demographics-card">
        <Flex justify="start">
          <ul style={{ fontWeight: 'bold', margin: 0, paddingLeft: 24, minWidth: 160 }}>
            <li data-testid="consent-first-name-item">{t('consentPage.consent-item-first-name')}</li>
            <li data-testid="consent-last-name-item">{t('consentPage.consent-item-last-name')}</li>
            <li data-testid="consent-birthdate-item">{t('consentPage.consent-item-birthdate')}</li>
            <li data-testid="consent-sex-item">{t('consentPage.consent-item-sex')}</li>
          </ul>
          <ul style={{ fontWeight: 'bold', margin: 0, paddingLeft: 15, minWidth: 160 }}>
            <li data-testid="consent-phone-number-item">{t('consentPage.consent-item-phone-number')}</li>
            <li data-testid="consent-email-item">{t('consentPage.consent-item-email')}</li>
            <li data-testid="consent-mrn-item">{t('consentPage.consent-item-mrn')}</li>
          </ul>
        </Flex>
      </Card>
      <Button
        type="primary"
        className="w-100"
        data-testid="consent-accept-btn"
        onClick={() => handleConsent(true)}
        autoFocus
      >
        {t('consentPage.consent-allow')}
      </Button>
      <Button
        type="default"
        className="w-100"
        data-testid="consent-decline-btn"
        onClick={() => handleConsent(false)}
      >
        {t('consentPage.consent-not-allow')}
      </Button>
      <Typography.Text
        style={{ padding: '16px 0', textAlign: 'center', display: 'block' }}
        data-testid="consent-description-2"
  >{t('consentPage.authorization-required-account-creation')}</Typography.Text>
    </Flex>
  );
};
export default ConsentPage;
